import React, { useCallback, useEffect, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

import { getZoneMembersOverview, type ZoneMembersEntry } from '../hooks/useIAM';
import ListStateBoundary from './iam/ListStateBoundary';

interface ZoneMembersOverviewProps {
  onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

/**
 * Consolidated "who is in each zone" admin view -- every zone shown
 * side-by-side, instead of opening each group's edit page one at a time.
 */
const ZoneMembersOverview: React.FC<ZoneMembersOverviewProps> = ({ onShowToast }) => {
  const [zones, setZones] = useState<ZoneMembersEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const refetch = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await getZoneMembersOverview();
      setZones(data);
    } catch {
      setError('Failed to load zone members');
      onShowToast('Failed to load zone members', 'error');
    } finally {
      setIsLoading(false);
    }
  }, [onShowToast]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          IAM &gt; Zone Members
        </h2>
        <button
          onClick={() => {
            void refetch();
          }}
          className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          title="Refresh"
        >
          <ArrowPathIcon className="h-5 w-5" />
        </button>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400">
        Every zone (group marked "Show in self-service Zones picker") and who currently belongs
        to it. To change membership, go to IAM &gt; Groups and edit the group directly, or approve
        a request from Zone Approvals.
      </p>

      <ListStateBoundary
        isLoading={isLoading}
        error={error}
        isEmpty={zones.length === 0}
        emptyMessage="No zones yet. Mark a group as a zone from IAM > Groups."
      >
        <div className="overflow-x-auto">
          <div className="flex gap-4 min-w-max pb-2">
            {zones.map((zoneEntry) => (
              <div
                key={zoneEntry.zone}
                className="min-w-[420px] flex-shrink-0 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-t-lg">
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {zoneEntry.zone}
                  </span>
                </div>
                {zoneEntry.members.length === 0 ? (
                  <div className="p-3 text-xs text-gray-400 dark:text-gray-500 italic min-h-[3rem]">
                    No members
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left font-medium px-3 py-2">Name</th>
                        <th className="text-left font-medium px-3 py-2">Email</th>
                        <th className="text-left font-medium px-3 py-2">First name</th>
                        <th className="text-left font-medium px-3 py-2">Last name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {zoneEntry.members.map((member) => (
                        <tr
                          key={member.username}
                          className="border-b border-gray-100 dark:border-gray-800 last:border-0"
                        >
                          <td className="px-3 py-2 text-primary-600 dark:text-primary-400">
                            {member.username}
                          </td>
                          <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                            {member.email || '—'}
                          </td>
                          <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                            {member.first_name || '—'}
                          </td>
                          <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                            {member.last_name || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            ))}
          </div>
        </div>
      </ListStateBoundary>
    </div>
  );
};

export default ZoneMembersOverview;
